import { FieldArray, FieldArrayRenderProps, FormikProps } from 'formik';
import React, { useContext, useState } from 'react';
import styled from 'styled-components/macro';
import { IfUserHasPermission } from '../../authorisation/IfUserHasPermission';
import { SecondaryButton } from '../../shared/Button';
import { EmptyPlaceholder } from '../../shared/EmptyPlaceholder';
import { FormContext } from '../../shared/form/Form';
import { SearchSelect, SearchSelectOptions } from '../../shared/form/inputs/SearchSelect';
import { Header4 } from '../../shared/Headers';
import { Table, TBody, Td, THead, Tr } from '../../shared/Table';
import { colourNeutral07 } from '../../styling/colours';
import { spacing02, spacing06 } from '../../styling/spacing';
import { EditDesignBillOfMaterialsFormModel } from '../designs/edit-bill-of-materials/editDesignBillOfMaterialsFormModel';
import { ViewOrEditQuoteBillOfMaterialsFormModel } from '../quotes/view-or-edit-bill-of-materials/viewOrEditQuoteBillOfMaterialsFormModel';
import { ActionButtonTh, CurrencyTh, DescriptionTh, PartCodeTh } from './BillOfMaterialsItemsTable';
import { BillOfMaterialsOptionalExtraField } from './BillOfMaterialsOptionalExtraField';
import { BillOfMaterialsOptionalExtraFormModel } from './billOfMaterialsOptionalExtraFormModel';
import { BillOfMaterialsPartsById } from './dataForEditBillOfMaterialsForm';
import { QuantityColumnHeader } from './QuantityColumnHeader';

type Props = {
  optionalExtrasFieldArrayName: string;
  label: string;
  optionalExtras: Array<BillOfMaterialsOptionalExtraFormModel>;
  partsById: BillOfMaterialsPartsById;
  partsOptions: SearchSelectOptions<number | null>;
  errors: FormikProps<EditDesignBillOfMaterialsFormModel | ViewOrEditQuoteBillOfMaterialsFormModel>['errors'];
};

export const BillOfMaterialsOptionalExtrasField = ({
  optionalExtrasFieldArrayName,
  optionalExtras,
  label,
  partsById,
  partsOptions,
  errors,
}: Props) => {
  const [selectedPartId, setSelectedPartId] = useState<number | null>(null);
  const { isDisabled } = useContext(FormContext);

  const addOptionalExtra = (arrayHelpers: FieldArrayRenderProps) => {
    if (selectedPartId == null) {
      return;
    }

    const newOptionalExtraPart: BillOfMaterialsOptionalExtraFormModel = {
      billOfMaterialsOptionalExtraId: null,
      partId: selectedPartId,
      quantity: '',
      partIsInactiveValidation: null,
    };

    arrayHelpers.push(newOptionalExtraPart);
  };

  return (
    <FieldArray name={optionalExtrasFieldArrayName}>
      {arrayHelpers => {
        return (
          <>
            <Header4>{label}</Header4>
            <StyledTable>
              <THead>
                <Tr>
                  <PartCodeTh>Part Code</PartCodeTh>
                  <QuantityColumnHeader fieldArrayName={optionalExtrasFieldArrayName} errors={errors} />
                  <DescriptionTh>Description</DescriptionTh>
                  <CurrencyTh>Price (£)</CurrencyTh>
                  <ActionButtonTh />
                </Tr>
              </THead>
              <TBody>
                {optionalExtras.map((extra, index) => (
                  <BillOfMaterialsOptionalExtraField
                    key={index}
                    fieldName={`${optionalExtrasFieldArrayName}.${index}`}
                    optionalExtra={extra}
                    partsById={partsById}
                    deleteOptionalExtra={() => arrayHelpers.remove(index)}
                    errors={errors}
                  />
                ))}
                {optionalExtras.length === 0 && (
                  <Tr>
                    <Td colSpan={5}>
                      <EmptyPlaceholder message={`No ${label} have been added to the Bill of Materials`} />
                    </Td>
                  </Tr>
                )}
              </TBody>
            </StyledTable>
            {!isDisabled && (
              <AddPartContainer>
                <AddPartSearchSelectContainer>
                  <SearchSelect<number | null>
                    onChange={setSelectedPartId}
                    value={selectedPartId}
                    options={partsOptions}
                    placeholder="Enter a Part Code"
                    minimumSearchCharacters={1}
                    testId={billOfMaterialsAddOptionalExtraPartSearchSelectTestId(optionalExtrasFieldArrayName)}
                  />
                </AddPartSearchSelectContainer>
                <SecondaryButton
                  onClick={() => addOptionalExtra(arrayHelpers)}
                  disabled={selectedPartId == null}
                  data-testid={billOfMaterialsAddOptionalExtraPartButtonTestId(optionalExtrasFieldArrayName)}
                >
                  Add Part
                </SecondaryButton>
              </AddPartContainer>
            )}
          </>
        );
      }}
    </FieldArray>
  );
};

export const billOfMaterialsAddOptionalExtraPartSearchSelectTestId = (optionalExtrasFieldArrayName: string) =>
  `add-part-search-select-${optionalExtrasFieldArrayName}`;

export const billOfMaterialsAddOptionalExtraPartButtonTestId = (optionalExtrasFieldArrayName: string) =>
  `add-part-button-${optionalExtrasFieldArrayName}`;

const StyledTable = styled(Table)`
  table-layout: fixed;
  background-color: ${colourNeutral07};
`;

const AddPartContainer = styled.div`
  margin-top: ${spacing06};
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-end;
`;

const AddPartSearchSelectContainer = styled.div`
  display: inline-block;
  position: relative;
  width: 500px;
  margin-right: ${spacing02};
`;
